```vue
<template>
  <div
    class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-modalFade"
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
  >
    <!-- กล่องโมดัล -->
    <div
      class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 rounded-xl shadow-2xl text-white overflow-hidden animate-scaleIn"
    >
      <!-- ส่วนหัว -->
      <div class="flex justify-between items-center p-5 border-b border-gray-700">
        <h2 id="modal-title" class="text-xl font-bold text-indigo-300">
          {{ ghost.name }}
        </h2>
        <button
          @click="$emit('cancelModal')"
          class="text-gray-400 hover:text-indigo-300 transition-colors duration-200"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-lg"></i>
        </button>
      </div>

      <!-- แท็บ -->
      <div class="flex border-b border-gray-700">
        <button
          @click="activeTab = 'info'"
          class="flex-1 py-3 px-4 text-center text-sm font-semibold transition-all duration-200"
          :class="{
            'bg-indigo-600 text-white border-b-2 border-indigo-500': activeTab === 'info',
            'bg-gray-700 text-indigo-200 hover:bg-gray-600': activeTab !== 'info',
          }"
          aria-selected="activeTab === 'info'"
        >
          ข้อมูลวิญญาณ
        </button>
        <button
          v-if="ghost.type === 'boss'"
          @click="activeTab = 'drops'"
          class="flex-1 py-3 px-4 text-center text-sm font-semibold transition-all duration-200"
          :class="{
            'bg-indigo-600 text-white border-b-2 border-indigo-500': activeTab === 'drops',
            'bg-gray-700 text-indigo-200 hover:bg-gray-600': activeTab !== 'drops',
          }"
          aria-selected="activeTab === 'drops'"
        >
          รางวัล
        </button>
        <button
          @click="activeTab = 'maps'"
          class="flex-1 py-3 px-4 text-center text-sm font-semibold transition-all duration-200"
          :class="{
            'bg-indigo-600 text-white border-b-2 border-indigo-500': activeTab === 'maps',
            'bg-gray-700 text-indigo-200 hover:bg-gray-600': activeTab !== 'maps',
          }"
          aria-selected="activeTab === 'maps'"
        >
          สถานที่
        </button>
      </div>

      <!-- เนื้อหา -->
      <div class="p-5 max-h-[32rem] overflow-y-auto scrollbar-thin">
        <!-- ข้อความข้อผิดพลาด -->
        <div v-if="error" class="text-red-400 text-sm text-center mb-4 bg-red-900/20 p-3 rounded-md">
          {{ error }}
        </div>

        <!-- แท็บ: ข้อมูลวิญญาณ -->
        <div v-if="activeTab === 'info'" class="fade-in">
          <div class="flex flex-col sm:flex-row gap-4">
            <div class="flex-shrink-0">
              <img
                :src="ghost.imageUrl || 'default-ghost.png'"
                alt="ภาพวิญญาณ"
                class="w-24 h-24 object-cover rounded-md border-2"
                :class="ghost.rarityColors?.borderColor || 'border-red-500'"
                @error="handleImageError"
              />
            </div>
            <div class="flex-1 space-y-4">
              <!-- สถิติ -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p class="text-xs text-indigo-200">เลเวล</p>
                  <p class="text-sm font-semibold text-indigo-300">{{ ghost.level }}</p>
                </div>
                <div>
                  <p class="text-xs text-indigo-200">ประเภท</p>
                  <p class="text-sm font-semibold text-indigo-300">{{ ghost.type === 'boss' ? 'บอส' : 'ทั่วไป' }}</p>
                </div>
              </div>
              <div>
                <p class="text-xs text-indigo-200 font-semibold">ค่าสถานะ</p>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 text-xs bg-gray-700 p-3 rounded-md">
                  <p class="flex items-center gap-1 text-indigo-200">
                    <i class="fas fa-heartbeat text-red-400"></i> พลังชีวิต: {{ calculatedStats.maxHp }}
                  </p>
                  <p class="flex items-center gap-1 text-indigo-200">
                    <i class="fas fa-fist-raised text-yellow-400"></i> พลังโจมตี: {{ calculatedStats.atk }}
                  </p>
                  <p class="flex items-center gap-1 text-indigo-200">
                    <i class="fas fa-running text-green-400"></i> การหลบหลีก: {{ calculatedStats.evasion }}
                  </p>
                  <p class="flex items-center gap-1 text-indigo-200">
                    <i class="fas fa-crosshairs text-purple-400"></i> คริติคอล: {{ calculatedStats.critical }}
                  </p>
                  <p class="flex items-center gap-1 text-indigo-200">
                    <i class="fas fa-shield-alt text-blue-400"></i> การป้องกัน: {{ calculatedStats.defense }}
                  </p>
                  <p class="flex items-center gap-1 text-indigo-200">
                    <i class="fas fa-clover text-lime-400"></i> การลดคริติคอล: {{ calculatedStats.criticalReduction }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- แท็บ: รางวัล (สำหรับบอสเท่านั้น) -->
        <div v-if="activeTab === 'drops' && ghost.type === 'boss'" class="fade-in max-h-96 overflow-auto" id="style-1">
          <h3 class="text-sm font-semibold text-indigo-300 mb-3">รางวัลที่ดรอป</h3>
          <div v-if="isLoadingDrops" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
            <p class="text-indigo-200 text-sm ml-2">กำลังโหลด...</p>
          </div>
          <div v-else-if="ghost.drops && ghost.drops.length > 0" class="space-y-3">
            <div
              v-for="(drop, index) in ghost.drops"
              :key="index"
              class="flex items-center gap-3 p-3 bg-gray-700 rounded-md border border-gray-600 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400"
            >
              <img
                v-if="itemDetails[drop.itemName]?.img"
                :src="itemDetails[drop.itemName].img"
                alt="Item Image"
                class="w-12 h-12 object-cover rounded-md border-2 border-indigo-400"
                @error="handleImageError"
              />
              <div v-else-if="isLoadingItem[drop.itemName]" class="w-12 h-12 flex items-center justify-center">
                <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600"></div>
              </div>
              <div v-else class="w-12 h-12 flex items-center justify-center bg-gray-600 rounded-md border-2 border-indigo-400">
                <i class="fas fa-image text-gray-400"></i>
              </div>
              <div class="flex-1">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-semibold text-indigo-300">{{ drop.itemName }}</p>
                  <i v-if="drop.transferable" class="fa-solid fa-arrow-right-arrow-left text-xs text-green-500"></i>
                </div>
                <p class="text-xs text-indigo-200">โอกาสดรอป: {{ (drop.dropChance / 100).toFixed(2) }}%</p>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-indigo-200 py-4 text-sm">
            ไม่มีรางวัลที่ดรอป
          </div>
        </div>

        <!-- แท็บ: สถานที่ -->
        <div v-if="activeTab === 'maps'" class="fade-in">
          <h3 class="text-sm font-semibold text-indigo-300 mb-3">แผนที่ที่มีวิญญาณตัวนี้</h3>
          <div v-if="isLoadingMaps" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
            <p class="text-indigo-200 text-sm ml-2">กำลังโหลด...</p>
          </div>
          <div v-else-if="maps.length > 0" class="space-y-3">
            <div
              v-for="map in maps"
              :key="map.id"
              class="flex items-center gap-3 p-3 bg-gray-700 rounded-md border border-gray-600 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400"
            >
              <img
                :src="map.imageUrl"
                alt="ภาพแผนที่"
                class="w-12 h-12 object-cover rounded-md border-2 border-indigo-400"
                @error="map.imageUrl = 'default-map.png'"
              />
              <div class="flex-1">
                <p class="text-sm font-semibold text-indigo-300">{{ map.name }}</p>
                <p class="text-xs text-indigo-200">โอกาสปรากฏ: {{ map.ghostChance }}%</p>
              </div>
            </div>
          </div>
          <div v-else class="text-center text-indigo-200 py-4 text-sm">
            ไม่พบแผนที่ที่มีวิญญาณตัวนี้
          </div>
        </div>
      </div>

      <!-- ส่วนท้าย -->
      <div class="flex justify-end gap-3 p-5 border-t border-gray-700 bg-gray-800">
        <button
          @click="$emit('cancelModal')"
          class="bg-gray-600 hover:bg-gray-700 text-sm text-white py-2 px-4 rounded-md transition-all duration-200 flex items-center gap-2 group disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i class="fas fa-times text-xs group-hover:animate-pulse"></i> ปิด
        </button>
        <button
          v-if="isAdmin"
          @click="$emit('editGhost', ghost)"
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-all duration-200 flex items-center gap-2 group disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i class="fas fa-edit text-xs group-hover:animate-pulse"></i> แก้ไข
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { projectFirestore } from '@/firebase/config';
import { usePlayerStore } from '@/composables/usePlayerStore';
import gameItemsData from '@/data/gameItems.json';

export default {
  props: {
    ghost: {
      type: Object,
      required: true,
    },
  },
  emits: ['cancelModal', 'editGhost'],
  setup(props) {
    const playerStore = usePlayerStore();
    const maps = ref([]);
    const activeTab = ref('info');
    const error = ref('');
    const isLoadingMaps = ref(false);
    const isLoadingDrops = ref(false);
    const isAdmin = ref(false);
    const currentGhostHp = ref(0);
    const itemDetails = ref({});
    const isLoadingItem = ref({});

    // จัดการข้อผิดพลาดเมื่อโหลดภาพ
    const handleImageError = (event) => {
      event.target.src = 'default-ghost.png';
    };

    // คำนวณค่าสถิติวิญญาณ
    const calculatedStats = computed(() => {
      const stats = props.ghost.stats || {};
      return {
        maxHp: (stats.stamina || 0) * 20 + (stats.maxHp || 0),
        atk: (stats.str || 0) * 2 + (stats.atk || 0),
        critical: Math.floor((stats.int || 0) / 2 + (stats.critical || 0)),
        evasion: Math.floor((stats.agi || 0) / 2 + (stats.evasion || 0)),
        defense: stats.defense || 0,
        criticalReduction: stats.luck || 0,
      };
    });

    // ดึงข้อมูลไอเทมจาก gameItems
    const fetchItemDetails = (itemName) => {
      if (!itemDetails.value[itemName] && !isLoadingItem.value[itemName]) {
        try {
          isLoadingItem.value[itemName] = true;
          itemDetails.value[itemName] = gameItemsData[itemName] || { img: 'default-item.png' };
        } catch (error) {
          console.error(`Error fetching item ${itemName}:`, error);
          itemDetails.value[itemName] = { img: 'default-item.png' };
        } finally {
          isLoadingItem.value[itemName] = false;
        }
      }
    };

    // ดึงข้อมูลแผนที่ที่มีวิญญาณตัวนี้
    const fetchMaps = async () => {
      try {
        isLoadingMaps.value = true;
        const snapshot = await projectFirestore
          .collection('maps')
          .where('ghostNames', 'array-contains', props.ghost.name)
          .get();

        maps.value = snapshot.docs.map((doc) => {
          const data = doc.data();
          const ghost = data.ghosts.find((g) => g.name === props.ghost.name);
          return {
            id: doc.id,
            name: data.name,
            imageUrl: data.imageUrl || 'default-map.png',
            ghostChance: ghost ? ghost.chance : 0,
          };
        });
      } catch (error) {
        console.error('เกิดข้อผิดพลาดในการดึงข้อมูลแผนที่:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลแผนที่';
      } finally {
        isLoadingMaps.value = false;
      }
    };

    // ดึงข้อมูลไอเทมที่ดรอป (สำหรับบอส)
    const fetchDrops = async () => {
      if (props.ghost.type === 'boss' && props.ghost.drops?.length > 0) {
        try {
          isLoadingDrops.value = true;
          props.ghost.drops.forEach(drop => fetchItemDetails(drop.itemName));
        } catch (error) {
          console.error('เกิดข้อผิดพลาดในการดึงข้อมูลไอเทมที่ดรอป:', error);
          error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลไอเทมที่ดรอป';
        } finally {
          isLoadingDrops.value = false;
        }
      }
    };

    // ตรวจสอบสถานะแอดมิน
    const checkAdminStatus = () => {
      isAdmin.value = playerStore.userData?.admin === true;
    };

    // ตั้งค่าเริ่มต้นสำหรับพลังชีวิตปัจจุบัน
    onMounted(async () => {
      currentGhostHp.value = calculatedStats.value.maxHp - (props.ghost.damageTaken || 0);
      await checkAdminStatus();
      await fetchMaps();
      await fetchDrops();
    });

    return {
      maps,
      activeTab,
      error,
      isLoadingMaps,
      isLoadingDrops,
      isAdmin,
      handleImageError,
      calculatedStats,
      currentGhostHp,
      itemDetails,
      isLoadingItem,
    };
  },
};
</script>

<style scoped>
/* Scrollbar แบบกำหนดเอง */
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4b5563 #374151;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* แอนิเมชัน */
@keyframes modalFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0.95);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}
.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}
.group:hover .group-hover\:animate-pulse {
  animation: pulse 1.5s infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.animate-spin {
  animation: spin 1s linear infinite;
}

/* สถานะปุ่ม */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

/* การปรับแต่งสำหรับหน้าจอขนาดเล็ก */
@media (max-width: 640px) {
  .text-xl {
    font-size: 1rem;
  }
  .text-sm {
    font-size: 0.75rem;
  }
  .text-xs {
    font-size: 0.625rem;
  }
  .p-5 {
    padding: 0.75rem;
  }
  .w-24 {
    width: 4.5rem;
    height: 4.5rem;
  }
  .max-h-\[32rem\] {
    max-height: 24rem;
  }
}
</style>
```